import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { useDebounce } from 'helpers'
import { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { ChevronDown, Edit } from 'react-feather'
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Spinner,
} from 'reactstrap'
import { useFetchGearsQuery } from './gearsListApiSlice'
import { Can } from 'context/Acl'
import PMBTooltip from 'components/PMBTooltip'
import { BUSINESS_VESSELS, MANAGE } from 'utils/subjectActions'
import { P } from 'components'
import IconUpdateGear from '../../../../assets/icons/icon-gears.svg'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { updateGear } from './gearsListAction'

const GearsTable = () => {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [isNoDataFilterAvailable, setIsNoDataFilterAvailable] = useState(false)
  const [modal, setModal] = useState({ edit: false })
  const [payload, setPayload] = useState({})
  const [loading, setLoading] = useState(false)

  // SORT BY FILTER
  const [sortColumn, setSortColumn] = useState('id')
  const [sortDirection, setSortDirection] = useState('DESC')

  const { data, isLoading, refetch, isFetching } = useFetchGearsQuery({
    page: currentPage ?? 0,
    size: rowsPerPage ?? 10,
    searchvalue: search,
    sortby: sortColumn ?? 'id',
    sortdir: sortDirection ?? 'DESC',
  })

  // HANDLE LOADING FILTER
  useEffect(() => {
    if (!isFetching && data?.totalItems === 0) {
      setIsNoDataFilterAvailable(true)
    } else {
      setIsNoDataFilterAvailable(false)
    }
  }, [isFetching, data?.totalItems])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSort = (column, direction) => {
    setSortColumn(column.selector)
    setSortDirection(direction?.toLowerCase() === 'asc' ? 'ASC' : 'DESC')
  }

  const columns = [
    {
      name: <span>ID</span>,
      selector: 'id',
      sortable: true,
      minWidth: '80px',
      cell: (row) => {
        return <span>{row?.id ?? '-'}</span>
      },
    },
    {
      name: <span>Gear Category</span>,
      selector: 'category',
      sortable: true,
      cell: (row) => {
        return <span>{row?.category ?? '-'}</span>
      },
      minWidth: '170px',
    },
    {
      name: <span>Sub</span>,
      selector: 'sub',
      sortable: true,
      cell: (row) => {
        return <span>{row?.sub ?? '-'}</span>
      },
      minWidth: '170px',
    },
    {
      name: <span>Type</span>,
      selector: 'typeVal',
      sortable: true,
      cell: (row) => {
        return <span>{row?.typeVal ?? '-'}</span>
      },
      minWidth: '170px',
    },
    {
      name: <span>Name</span>,
      selector: 'name',
      sortable: true,
      cell: (row) => {
        return <span>{row?.name ?? '-'}</span>
      },
      minWidth: '170px',
    },
    {
      name: <span>MMAF</span>,
      selector: 'mmaf',
      sortable: true,
      cell: (row) => {
        return <span>{row?.mmaf ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>ISSCF</span>,
      selector: 'isscf',
      sortable: true,
      cell: (row) => {
        return <span>{row?.isscf ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>FAO</span>,
      selector: 'fao',
      sortable: true,
      cell: (row) => {
        return <span>{row?.fao ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>GDST</span>,
      selector: 'fao',
      sortable: true,
      cell: (row) => {
        return <span>{row?.fao ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span></span>,
      selector: 'fao',
      sortable: true,
      cell: (row) => {
        return (
          <Can I={MANAGE} this={BUSINESS_VESSELS}>
            <div
              className="d-flex align-items-center"
              style={{ gap: '12px', flexGrow: 1 }}
              data-testid={`gear-table-row-${row?.id}`}
            >
              <div id={`vessel-${row?.id}`}>
                <Edit
                  size={18}
                  onClick={() => {
                    setPayload(row)
                    setModal((prevState) => ({ ...prevState, edit: true }))
                  }}
                  style={{ cursor: 'pointer' }}
                  data-testid={`gear-row-edit-${row?.id}`}
                />
              </div>
              <PMBTooltip target={`vessel-${row?.id}`} text="Ubah" />
            </div>
          </Can>
        )
      },
      minWidth: '80px',
    },
  ]

  const customStyles = {
    headCells: {
      style: {
        color: '#667085',
        backgroundColor: '#F9FAFB',
      },
    },
    cells: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
      },
    },
    rows: {
      style: {
        marginTop: '1px',
        marginBottom: '1px',
        '&:hover': {
          backgroundColor: '#F9FAFB',
        },
      },
    },
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && search.length === 0) {
      e.preventDefault()
    }
  }

  const debouncedSearch = useDebounce((value) => {
    setCurrentPage(0)
    setRowsPerPage(10)
    setSearch(value)
    refetch()
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const onHandleUpdate = async () => {
    try {
      setLoading(true)
      const result = await dispatch(updateGear(payload)).unwrap()
      refetch()
      enqueueSnackbar('Data berhasil diubah!', {
        variant: 'success',
      })
      setModal((prevState) => ({ ...prevState, edit: false }))
    } catch (err) {
      enqueueSnackbar('Data gagal diubah!', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs breadCrumbActive="Data Gears" />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            customStyles={customStyles}
            columns={columns}
            data={data?.items}
            progressPending={isLoading || isFetching}
            responsive
            persistTableHead
            defaultSortFieldId={1}
            defaultSortAsc={false}
            paginationTotalRows={data?.totalItems}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            sortIcon={<ChevronDown />}
            sortServer
            onSort={handleSort}
            className="border p-0 border-1 rounded-top"
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isNoDataFilterAvailable
                  ? 'Tidak ada data yang sesuai dengan filter Anda.'
                  : 'Belum ada daftar gears yang didaftarkan'}
              </p>
            }
            pagination
            paginationServer
            paginationComponent={(props) => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                setCurrentPage={setCurrentPage}
                setRowsPerPage={setRowsPerPage}
                props={props}
                testId="gear-pagination"
              />
            )}
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">Daftar Gears</h5>
                </div>

                <div className="my-4">
                  <PMBSearchInputGroup
                    label={'Cari'}
                    value={searchInput}
                    placeholder="Cari nama gear..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth="25%"
                    onKeyDown={handleKeyDown}
                    testId="gear-search-input"
                  />
                </div>
              </div>
            }
          />
        </CardBody>
      </Card>
      {renderModal()}
    </div>
  )

  function renderModal() {
    return (
      <>
        <Modal
          centered
          isOpen={modal?.edit}
          toggle={() =>
            setModal((prevState) => ({ ...prevState, edit: false }))
          }
          data-testid="gear-modal-edit"
        >
          <ModalBody style={{ overflowY: 'auto', maxHeight: '90vh' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              {/* Outer circle - #ecfaff */}
              <div
                style={{
                  width: 60,
                  height: 60,
                  backgroundColor: '#ecfaff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Inner circle - #d6eaf1 */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#d6eaf1',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={IconUpdateGear}
                    loading="lazy"
                    style={{
                      width: 24,
                      height: 24,
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
            </div>
            <P size="medium">Ubah data Gear</P>
            <div className="mt-3">
              <FormGroup>
                <Label className="text-sm">Name</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan nama"
                  autoComplete="off"
                  name="name"
                  id="name"
                  data-testid="gear-form-name"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.name ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">Category</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan kategori"
                  autoComplete="off"
                  name="category"
                  id="category"
                  data-testid="gear-form-category"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.category ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">Sub</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan sub"
                  autoComplete="off"
                  name="sub"
                  id="sub"
                  data-testid="gear-form-sub"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.sub ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">Type Value</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan nilai tipe"
                  autoComplete="off"
                  name="typeVal"
                  id="typeVal"
                  data-testid="gear-form-type"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.typeVal ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">MMAF</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan MMAF"
                  autoComplete="off"
                  name="mmaf"
                  id="mmaf"
                  data-testid="gear-form-mmaf"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.mmaf ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">ISSCF</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan ISSCF"
                  autoComplete="off"
                  name="isscf"
                  id="isscf"
                  data-testid="gear-form-isscf"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.isscf ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">FAO</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan FAO"
                  autoComplete="off"
                  name="fao"
                  id="fao"
                  data-testid="gear-form-fao"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value?.toUpperCase(),
                    }))
                  }}
                  value={payload?.fao ?? ''}
                />
              </FormGroup>

              <FormGroup>
                <Label className="text-sm">GDST URI</Label>
                <Input
                  className="custom-placeholder"
                  placeholder="Masukkan GDST URI"
                  autoComplete="off"
                  name="gdstUri"
                  id="gdstUri"
                  data-testid="gear-form-gdst-uri"
                  onChange={(e) => {
                    setPayload((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }))
                  }}
                  value={payload?.gdstUri ?? ''}
                />
              </FormGroup>

              <div className="d-flex gap-2">
                <Button
                  disabled={loading}
                  color="primary"
                  outline
                  style={{ flex: 0.49 }}
                  onClick={() =>
                    setModal((prevState) => ({ ...prevState, edit: false }))
                  }
                  data-testid="gear-modal-edit-cancel-button"
                >
                  <P>Batal</P>
                </Button>
                <Button
                  disabled={loading}
                  color="primary"
                  style={{ flex: 0.49 }}
                  onClick={onHandleUpdate}
                  data-testid="gear-modal-edit-submit-button"
                >
                  <P>Simpan</P>
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </>
    )
  }
}

export default GearsTable
